import Column from '@app/components/layout/Column'
import { Form } from '@app/components/core/form/Form'
import { TextAreaField } from '@app/components/core/form/fields/TextAreaField'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useMemo } from 'react'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { useEditContainerClose } from '../shared/useEditContainerClose'
import { FieldEditActions } from '../shared/FieldEditActions'
import type { UseFormReturn } from 'react-hook-form'
import { enrich } from '@i18n/localizeHelper'
import {
  createVisionSchema,
  type VisionFormValues,
  VISION_MIN_LENGTH,
} from '@app/pages/dashboard/initiative/shared/validation/initiativeValidation'

export type MainDetailsVisionEditProps = {
  initiativeId: number
  vision: string
}

const VisionFormBody = ({
  methods,
  vision,
  creation,
  onCancel,
}: {
  methods: UseFormReturn<VisionFormValues>
  vision: string
  creation: { contentLabel: string; contentPlaceholder: string }
  onCancel: () => void
}) => {
  const { reset, formState, trigger } = methods

  useEffect(() => {
    reset({ vision })
    void trigger()
  }, [vision, reset, trigger])

  return (
    <Column gap="1rem" alignItems="stretch">
      <TextAreaField<VisionFormValues>
        name="vision"
        label={creation.contentLabel}
        placeholder={creation.contentPlaceholder}
      />
      <FieldEditActions
        onCancel={onCancel}
        saveDisabled={!formState.isValid || formState.isSubmitting}
      />
    </Column>
  )
}

export const MainDetailsVisionEdit = ({
  initiativeId,
  vision,
}: MainDetailsVisionEditProps) => {
  const close = useEditContainerClose()
  if (!close) {
    throw new Error('MainDetailsVisionEdit requires an EditContainer ancestor')
  }
  const client = useClient()
  const { showFailToast, showSuccessToast } = useToast()
  const common = useTranslations().app.common
  const creation = useTranslations().app.dashboard.initiative.creation
  const formValidation = useTranslations().app.form.validation

  const schema = useMemo(
    () =>
      createVisionSchema(enrich(formValidation.minLength, VISION_MIN_LENGTH)),
    [formValidation.minLength],
  )

  const onSubmit = useCallback(
    (data: VisionFormValues) => {
      void client
        .call('edit-initiative', {
          id: initiativeId,
          value: {
            vision: data.vision.trim(),
          },
        })
        .then((success) => {
          if (success) {
            showSuccessToast(common.toastSuccess)
            close()
          } else {
            showFailToast()
          }
        })
        .catch(() => showFailToast())
    },
    [
      client,
      initiativeId,
      close,
      showFailToast,
      showSuccessToast,
      common.toastSuccess,
    ],
  )

  return (
    <Form<VisionFormValues>
      schema={schema}
      defaultValues={{ vision }}
      mode="onChange"
      onSubmit={onSubmit}
    >
      {(methods) => (
        <VisionFormBody
          methods={methods}
          vision={vision}
          creation={creation}
          onCancel={() => {
            methods.reset({ vision })
            close()
          }}
        />
      )}
    </Form>
  )
}
